import { defaultTenantBrandingPolicy, defaultThemeValues, layerOrder, themeCapabilityRegistry, themePropertyDefinitions, themePropertyKeys, canLayerOverrideProperty, } from "./catalog";
import { currentThemeSchemaVersion, currentThemeVersion, } from "./contracts";
import { defaultThemeSources } from "./defaults";
import { themeMigrationRegistry } from "./migration";
import { validateResolvedThemeAccessibility, validateThemePropertyValue, themeValidationRulesetVersion, } from "./validation";
const rolePriority = (source) => source.scope.kind === "role" ? source.scope.priority : 0;
const orderSources = (sources) => [...sources].sort((first, second) => {
    const layerDifference = layerOrder[first.layer] - layerOrder[second.layer];
    if (layerDifference !== 0)
        return layerDifference;
    if (first.layer === "role" && second.layer === "role") {
        const priorityDifference = rolePriority(first) - rolePriority(second);
        if (priorityDifference !== 0)
            return priorityDifference;
    }
    return first.sourceId.localeCompare(second.sourceId);
});
const sourceValues = (source) => source.preference?.values ?? source.definition?.values ?? {};
const sourceMigration = (source, timestamp) => {
    if (!source.preference) {
        return {
            source,
            diagnostics: [],
            usable: true,
        };
    }
    const migration = themeMigrationRegistry.migrate(source.preference, timestamp);
    if (migration.status === "failed" ||
        migration.status === "unsupported-future") {
        return {
            source,
            diagnostics: migration.diagnostics,
            usable: false,
        };
    }
    return {
        source: { ...source, preference: migration.preference },
        diagnostics: migration.diagnostics,
        usable: true,
    };
};
const fingerprint = (values) => {
    const serialized = themePropertyKeys
        .map((key) => `${key}:${JSON.stringify(values[key])}`)
        .join("|");
    let hash = 2166136261;
    for (let index = 0; index < serialized.length; index += 1) {
        hash ^= serialized.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }
    return `iaf-${(hash >>> 0).toString(16).padStart(8, "0")}`;
};
const validationIssue = (source, key, code, message) => ({
    issueId: `${source.sourceId}:${key}:${code}`,
    code,
    severity: "error",
    message,
    propertyKeys: [key],
    sourceId: source.sourceId,
    layer: source.layer,
    remediation: "Remove the override or choose an approved semantic value.",
});
const applyEnvironmentPalette = (values, mode, highContrast, shouldDerive) => {
    const assign = (palette) => {
        for (const [untrustedKey, value] of Object.entries(palette)) {
            if (themeCapabilityRegistry.isRegistered(untrustedKey) &&
                (highContrast || shouldDerive(untrustedKey))) {
                values[untrustedKey] =
                    value;
            }
        }
    };
    if (mode === "dark") {
        assign({
            "color.page.background": highContrast ? "#000000" : "#0e1b28",
            "color.surface.default": highContrast ? "#000000" : "#152536",
            "color.surface.raised": highContrast ? "#0a0a0a" : "#1d3042",
            "color.surface.sunken": "#09141f",
            "color.surface.overlay": highContrast ? "#000000" : "#203549",
            "color.text.primary": "#ffffff",
            "color.text.secondary": highContrast ? "#ffffff" : "#d2dce5",
            "color.text.muted": highContrast ? "#ffffff" : "#b7c5d2",
            "color.text.inverse": "#000000",
            "color.text.disabled": "#91a3b3",
            "color.text.link": "#8bc7ff",
            "color.interaction.hover": highContrast ? "#262626" : "#203b52",
            "color.interaction.selected": highContrast ? "#333333" : "#274c69",
            "color.interaction.focus": highContrast ? "#ffeb3b" : "#69b8ff",
            "color.interaction.disabled": "#30465a",
            "color.interaction.drag": "#9ad2ff",
            "color.status.information": "#69b8ff",
            "color.status.success": "#5fd49a",
            "color.status.warning": "#ffbd66",
            "color.status.error": "#ff8a80",
            "color.status.neutral": "#b7c5d2",
            "color.border.default": highContrast ? "#ffffff" : "#71869a",
            "color.border.subtle": highContrast ? "#ffffff" : "#30465a",
            "color.border.strong": "#ffffff",
            "color.border.focus": highContrast ? "#ffeb3b" : "#69b8ff",
            "color.border.error": "#ff8a80",
            "chart.axis": highContrast ? "#ffffff" : "#b7c5d2",
            "chart.grid": highContrast ? "#ffffff" : "#30465a",
            "chart.label": "#ffffff",
            "chart.tooltip": highContrast ? "#000000" : "#152536",
        });
        return;
    }
    if (highContrast) {
        assign({
            "color.page.background": "#ffffff",
            "color.surface.default": "#ffffff",
            "color.surface.raised": "#ffffff",
            "color.surface.sunken": "#f2f2f2",
            "color.surface.overlay": "#ffffff",
            "color.text.primary": "#000000",
            "color.text.secondary": "#000000",
            "color.text.muted": "#1f1f1f",
            "color.text.disabled": "#595959",
            "color.text.link": "#004f87",
            "color.interaction.focus": "#005fcc",
            "color.border.default": "#000000",
            "color.border.subtle": "#595959",
            "color.border.strong": "#000000",
            "color.border.focus": "#005fcc",
            "chart.axis": "#000000",
            "chart.grid": "#595959",
            "chart.label": "#000000",
            "chart.tooltip": "#ffffff",
        });
    }
};
export const resolveTheme = (request) => {
    const resolvedAt = request.resolvedAt ?? new Date().toISOString();
    const ordered = orderSources([
        ...defaultThemeSources,
        ...request.sources.filter((source) => !defaultThemeSources.some((defaultSource) => defaultSource.sourceId === source.sourceId)),
    ]);
    const values = { ...defaultThemeValues };
    const attempts = new Map();
    const migrationNotes = new Map();
    const sourceProvenance = [];
    const issues = [];
    const diagnostics = [];
    for (const definition of themePropertyDefinitions) {
        attempts.set(definition.key, []);
        migrationNotes.set(definition.key, []);
    }
    for (const originalSource of ordered) {
        const migrated = sourceMigration(originalSource, resolvedAt);
        const acceptedProperties = [];
        const rejectedProperties = [];
        diagnostics.push(...migrated.diagnostics);
        if (!migrated.usable) {
            sourceProvenance.push({
                sourceId: originalSource.sourceId,
                layer: originalSource.layer,
                scope: originalSource.scope,
                acceptedProperties,
                rejectedProperties,
                diagnostics: migrated.diagnostics,
            });
            continue;
        }
        const source = migrated.source;
        for (const [untrustedKey, value] of Object.entries(sourceValues(source))) {
            if (!themeCapabilityRegistry.isRegistered(untrustedKey)) {
                diagnostics.push(`${source.sourceId} supplied unknown ${untrustedKey}; it was ignored.`);
                continue;
            }
            const key = untrustedKey;
            const propertyValue = value;
            const migrationMessages = migrationNotes.get(key);
            migrationMessages?.push(...migrated.diagnostics);
            if (!canLayerOverrideProperty(source.layer, key, defaultTenantBrandingPolicy)) {
                const reason = `${source.layer} is not allowed to override ${key}.`;
                attempts.get(key)?.push({
                    sourceId: source.sourceId,
                    layer: source.layer,
                    scope: source.scope,
                    value: propertyValue,
                    accepted: false,
                    reason,
                });
                rejectedProperties.push(key);
                issues.push(validationIssue(source, key, "scope-not-allowed", reason));
                continue;
            }
            const validation = validateThemePropertyValue(key, propertyValue, {
                layer: source.layer,
                sourceId: source.sourceId,
            });
            if (!validation.valid) {
                const reason = validation.message ?? `${key} is invalid.`;
                attempts.get(key)?.push({
                    sourceId: source.sourceId,
                    layer: source.layer,
                    scope: source.scope,
                    value: propertyValue,
                    accepted: false,
                    reason,
                });
                rejectedProperties.push(key);
                issues.push(validationIssue(source, key, validation.code ?? "invalid", reason));
                continue;
            }
            attempts.get(key)?.push({
                sourceId: source.sourceId,
                layer: source.layer,
                scope: source.scope,
                value: propertyValue,
                accepted: true,
            });
            values[key] =
                propertyValue;
            acceptedProperties.push(key);
        }
        for (const unknown of source.preference?.unknownProperties ?? source.definition?.unknownProperties ?? []) {
            diagnostics.push(`${source.sourceId} preserved unknown ${unknown.key}; it did not affect resolution.`);
        }
        sourceProvenance.push({
            sourceId: source.sourceId,
            layer: source.layer,
            scope: source.scope,
            acceptedProperties,
            rejectedProperties,
            diagnostics: migrated.diagnostics,
        });
    }
    const mode = values["appearance.mode"];
    const resolvedMode = mode === "system"
        ? request.environment.prefersDark
            ? "dark"
            : "light"
        : mode;
    values["appearance.colorScheme"] = resolvedMode;
    const highContrast = values["appearance.contrastMode"] === "high" ||
        values["accessibility.highContrast"] ||
        (values["appearance.contrastMode"] === "system" &&
            request.environment.prefersHighContrast) ||
        request.environment.forcedColorsActive;
    values["accessibility.highContrast"] = highContrast;
    applyEnvironmentPalette(values, resolvedMode, highContrast, (key) => {
        const accepted = (attempts.get(key) ?? []).filter((attempt) => attempt.accepted);
        return accepted.length === 0 || accepted.at(-1)?.layer === "iaf";
    });
    const reducedMotion = values["accessibility.reducedMotion"] ||
        values["motion.reduced"] ||
        request.environment.prefersReducedMotion;
    values["accessibility.reducedMotion"] = reducedMotion;
    if (reducedMotion) {
        values["motion.enabled"] = false;
        values["motion.durationScale"] = 0;
    }
    const properties = themePropertyKeys.map((key) => {
        const propertyAttempts = attempts.get(key) ?? [];
        const accepted = propertyAttempts.filter((attempt) => attempt.accepted);
        const rejected = propertyAttempts.filter((attempt) => !attempt.accepted);
        const winner = accepted.at(-1) ?? {
            sourceId: "iaf:defaults",
            layer: "iaf",
            scope: { kind: "iaf" },
            value: defaultThemeValues[key],
            accepted: true,
        };
        const definition = themeCapabilityRegistry.getPropertyDefinition(key);
        const provenance = {
            propertyKey: key,
            winningSourceId: winner.sourceId,
            winningLayer: winner.layer,
            winningScope: winner.scope,
            inheritedSourceChain: propertyAttempts,
            overriddenValues: accepted.slice(0, -1),
            invalidIgnoredValues: rejected,
            protected: definition?.protected ?? false,
            validationNotes: key === "appearance.colorScheme"
                ? [`Resolved ${resolvedMode} from ${mode} mode and environment.`]
                : key === "accessibility.reducedMotion"
                    ? [
                        `Resolved reduced motion as ${String(reducedMotion)} from preferences and environment.`,
                    ]
                    : [],
            migrationNotes: migrationNotes.get(key) ?? [],
        };
        return {
            propertyKey: key,
            value: values[key],
            provenance,
        };
    });
    const accessibilityIssues = validateResolvedThemeAccessibility(values);
    const blocking = [
        ...issues,
        ...accessibilityIssues,
    ].some((candidate) => candidate.severity === "error" ||
        candidate.severity === "blocking-accessibility");
    const validation = {
        rulesetVersion: themeValidationRulesetVersion,
        schemaVersion: currentThemeSchemaVersion,
        valid: !blocking,
        canPreview: true,
        canSave: !blocking,
        issues,
        accessibilityIssues,
        validProperties: themePropertyKeys.filter((key) => !issues.some((candidate) => candidate.propertyKeys.includes(key))),
        invalidProperties: themePropertyKeys.filter((key) => issues.some((candidate) => candidate.propertyKeys.includes(key))),
        unknownProperties: ordered.flatMap((source) => source.preference?.unknownProperties ??
            source.definition?.unknownProperties ??
            []),
    };
    const appliedSourceIds = sourceProvenance
        .filter((source) => source.acceptedProperties.length > 0)
        .map((source) => source.sourceId);
    const theme = {
        schemaVersion: currentThemeSchemaVersion,
        themeVersion: currentThemeVersion,
        resolvedAt,
        fingerprint: fingerprint(values),
        mode: resolvedMode,
        values,
        properties,
        sourceProvenance,
        appliedSourceIds,
        diagnostics,
        compatibility: {
            schemaVersion: currentThemeSchemaVersion,
            themeVersion: currentThemeVersion,
            minimumSupportedIafVersion: "0.1.0",
            maximumTestedIafVersion: request.iafVersion,
            migrationStatus: diagnostics.some((diagnostic) => diagnostic.startsWith("Migrated"))
                ? "migrated"
                : "not-required",
        },
        validation,
    };
    return {
        status: diagnostics.length > 0 || issues.length > 0
            ? "resolved-with-diagnostics"
            : "resolved",
        theme,
    };
};
//# sourceMappingURL=resolution.js.map